import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import KeyValueList from '@/components/ui/KeyValueList'
import SectionCard from '@/components/ui/SectionCard'
import SectionHead from '@/components/ui/SectionHead'
import Skeleton from '@/components/ui/Skeleton'
import { useSuspenseDictionaryTerm } from '@/features/courses/api/queries'
import { parseSlug } from '@/screens/courses/lib/params'
import DictionaryExampleCard from '@/screens/courses/components/dictionary/DictionaryExampleCard'
import DictionarySearchBox from '@/screens/courses/components/dictionary/DictionarySearchBox'
import { LEARN_COLUMNS, LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import LearnHead, { LearnHeadSkeleton } from '@/screens/courses/components/learn/LearnHead'
import { TERM_CHIP_LINK } from '@/screens/courses/components/dictionary/term-chip'
import { loadDictionaryTerm } from '@/screens/courses/DictionaryTermScreen.loader'

function TermNotFound() {
  return (
    <ScreenLayout {...LEARN_PAGE} pageTitle="Term not found">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Term not found"
        description="This word is not in the dictionary, or the link is wrong."
        action={
          <Link to="/learn/dictionary" className={stateActionClass('page')}>
            Back to the dictionary
          </Link>
        }
      />
    </ScreenLayout>
  )
}

function DictionaryTermPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading this term…"
      header={<LearnHeadSkeleton />}
    >
      <div aria-hidden="true" className={LEARN_COLUMNS}>
        <div className="flex flex-col gap-8">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    </ScreenLayout>
  )
}

function DictionaryTermError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      fullWidth
      header={<LearnHeadSkeleton />}
      title="This term did not load"
    />
  )
}

export const Route = createFileRoute('/_app/learn/dictionary/$slug')({
  loader: loadDictionaryTerm,
  pendingComponent: DictionaryTermPending,
  errorComponent: DictionaryTermError,
  component: DictionaryTermRoute,
})

/** A slug mm-be would refuse never reaches the suspense read (the loader skipped it too). */
function DictionaryTermRoute() {
  const slug = parseSlug(Route.useParams().slug)
  if (slug === null) return <TermNotFound />
  return <DictionaryTermScreen key={slug} slug={slug} />
}

/**
 * Learn · one dictionary term (`GET /dictionary/{slug}/`). `null`
 * (`DICTIONARY_TERM_NOT_FOUND`) is `TermNotFound`, not a failed page.
 *
 * The title row carries the word, then its Nepali spelling and short
 * definition as one line under it (`termNepali` in its own `lang="ne"` span,
 * so assistive tech switches script there), and the dictionary search at the
 * right. Below, two columns: the term's own panels — "In one line" (`oneLine`,
 * the sentence that explains it, distinct from the short definition above),
 * "With numbers" (`facts`) and "Related words" — and "Where you'll see it", a
 * real company that trades the word's way.
 */
function DictionaryTermScreen({ slug }: { slug: string }) {
  const { data: term } = useSuspenseDictionaryTerm(slug)
  if (term === null) return <TermNotFound />

  return (
    <ScreenLayout
      {...LEARN_PAGE}
      header={
        // rest:ln (mock `dx-head`): no back — the strip above, Dictionary lit, is the way
        // back — and the search level with the title, 320 wide.
        <LearnHead
          title={term.term}
          sub={
            <>
              {term.termNepali === null ? null : <span lang="ne">{term.termNepali}. </span>}
              {term.shortDef}
            </>
          }
          aside={<DictionarySearchBox className="w-80 max-sm:hidden" />}
          asideAlign="start"
        />
      }
      pageTitle={term.term}
    >
      <DictionarySearchBox className="sm:hidden" />
      <div className={LEARN_COLUMNS}>
        <div className="flex min-w-0 flex-col gap-8">
          <section className="flex flex-col gap-3">
            <SectionHead title="In one line" size="card" />
            <SectionCard className="p-5">
              <p className="text-body-l text-foreground">{term.oneLine}</p>
            </SectionCard>
          </section>

          {term.facts.length === 0 ? null : (
            <section className="flex flex-col gap-3">
              <SectionHead title="With numbers" size="card" />
              <SectionCard className="py-0">
                <KeyValueList
                  density="roomy"
                  rows={term.facts.map((fact) => ({
                    label: fact.label,
                    // CMS free text, not a figure — KeyValueList's `<dd>` is
                    // `whitespace-nowrap` by default, but this may run longer
                    // than a number ever would.
                    value: <span className="whitespace-normal">{fact.value}</span>,
                  }))}
                />
              </SectionCard>
            </section>
          )}

          {term.related.length === 0 ? null : (
            <section className="flex flex-col gap-3">
              <SectionHead title="Related words" size="card" />
              <div className="flex flex-wrap gap-2">
                {term.related.map((related) => (
                  <Link
                    key={related.slug}
                    to="/learn/dictionary/$slug"
                    params={{ slug: related.slug }}
                    className={TERM_CHIP_LINK}
                  >
                    {related.term}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {term.example === null ? null : (
          <section className="flex min-w-0 flex-col gap-3">
            <SectionHead title="Where you'll see it" size="card" />
            <DictionaryExampleCard example={term.example} />
          </section>
        )}
      </div>
    </ScreenLayout>
  )
}
